import {
  ExclamationCircleFilled,
  FilePdfOutlined,
  PushpinOutlined,
} from "@ant-design/icons";
import { Input, Modal, Spin, message as antdMessage } from "antd";
import { EasyAvatar } from "fin-web-components";
import { observer } from "mobx-react-lite";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useNavigate } from "react-router-dom";
import { MemberNickname } from "../../components/member-badge/MemberNickname";
import { globalModel } from "../../global/GlobalModel";
import { uploadAppFileToOss } from "../../global/OssUploadApi";
import { useCsKeyboardLayout } from "../../space/customer-service/useCsKeyboardLayout";
import { CS_ASSISTANT_AVATAR_URL } from "../../space/customer-service/SpaceCustomerServiceTypes";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { getReadableTime, parseReadableDate } from "../../utils/getReadableTime";
import { compressFileToJpegFile } from "../../utils/imageCompressToJpeg";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";
import { stripOssImageProcess } from "fin-web-components";
import { useSafeAreaTopPad } from "../../utils/useSafeAreaTopPad";
import { h5NavigateTo } from "../../utils/miniProgramEnv";
import { SpaceApi } from "../../space/SpaceApi";
import { SpaceMessageCenterApi } from "../../space/messages/SpaceMessageCenterApi";
import { isH5MobileProductSupported } from "../../space/product/productServiceUtils";
import { showDesktopOnlyTipModal } from "../../space/common/DesktopOnlyTipModal";
import { showChatMentionNavigateModal } from "./ChatMentionNavigateModal";
import {
  handleChatMentionTargetClick,
  isChatMentionClickTarget,
  toChatMentionProductsFromStartup,
  type ChatMentionProduct,
} from "./chatMentionUtil";
import { CompanionReplyQuoteBlock, CompanionRoomMessageContent, quotePreviewText } from "./CompanionRoomMessageContent";
import { CompanionRoomModel } from "./CompanionRoomModel";
import { ChatStickerApi } from "./ChatStickerApi";
import type { ChatStickerGroupBundle, ChatStickerItem } from "./ChatStickerType";
import {
  COMPANION_QUOTA_BAR,
  COMPANION_REPLY_ACTION,
  COMPANION_REPLY_TIP_BAR,
  COMPANION_STICKER_PANEL,
} from "./Config";
import {
  companionIsAssistantSystemMessage,
  companionIsSelfModerationRejected,
  companionMessageIsMine,
  type CompanionMessageRow,
  type MyMessageThreadRow,
} from "./CompanionRoomType";
import "./CompanionRoomStyle.css";
import "../../space/customer-service/SpaceCustomerServiceStyle.css";

type ComposeReply = { messageId: string; senderLabel: string; quotedFull: string };
type ComposeImageDraft = { fileUrl: string; fileName: string };

/** 消息中心已读游标：取公屏最新服务端消息 id（忽略本地乐观消息） */
function companionLatestServerMsgId(list: CompanionMessageRow[]): string | undefined {
  let best: string | undefined;
  for (const m of list) {
    const id = m?.id?.trim();
    if (!id || id.startsWith("local-")) continue;
    if (!best || id.localeCompare(best, undefined, { numeric: true }) > 0) {
      best = id;
    }
  }
  return best;
}

const COMPANION_IMAGE_COMPRESS_MAX_EDGE = 1280;
const COMPANION_IMAGE_COMPRESS_QUALITY = 0.86;

function companionUploadErrorMessage(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e ?? "");
  const msg = raw.trim();
  if (!msg) return "图片上传失败";
  // Safari / 微信：fetch 网络层失败常为 Load failed / Failed to fetch
  if (/^load failed$/i.test(msg) || /failed to fetch/i.test(msg) || /networkerror/i.test(msg)) {
    return "图片上传失败，请检查网络后重试";
  }
  return msg;
}

async function prepareCompanionImageUploadFile(file: File): Promise<File> {
  try {
    return await compressFileToJpegFile(
      file,
      COMPANION_IMAGE_COMPRESS_MAX_EDGE,
      COMPANION_IMAGE_COMPRESS_QUALITY
    );
  } catch {
    // HEIC 等格式在部分环境 canvas 解码失败时回退原图
    return file;
  }
}

function shouldShowTimeDivider(prev?: CompanionMessageRow, curr?: CompanionMessageRow): boolean {
  if (!curr?.sentAt) return false;
  if (!prev?.sentAt) return true;
  const a = parseReadableDate(prev.sentAt).getTime();
  const b = parseReadableDate(curr.sentAt).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  return b - a > 5 * 60 * 1000;
}

function pinPreviewLine(m: CompanionMessageRow): string {
  if (m.messageType === "IMAGE") return "[图片]";
  if (m.messageType === "STICKER") return "[表情]";
  if (m.messageType === "PDF") return m.fileName?.trim() || "[PDF]";
  return (m.content || "").trim() || "消息";
}

function companionStaffRoleLabel(code?: string | null): string {
  const c = (code ?? "").trim().toLowerCase();
  if (c === "teacher") return "老师";
  if (c === "assistant") return "助理";
  return "";
}

function messageSenderLabel(
  m: CompanionMessageRow,
  selfName: string,
  selfUserId?: string
): string {
  if (m.fromUser) {
    const sid = m.senderUserId?.trim() ?? "";
    const uid = selfUserId?.trim() ?? "";
    if (uid && sid && sid === uid) return selfName;
    return m.senderName?.trim() || "用户";
  }
  const role = companionStaffRoleLabel(m.senderSpaceRole);
  const name = m.senderName?.trim() || "老师";
  return role ? `${name}（${role}）` : name;
}

const CompanionRoomPage: React.FC<{
  spaceId: string;
  spaceProductId: string;
  productDisplayName?: string;
  spaceDisplayName?: string;
  /** 嵌入消息中心聊天壳：去掉 TabBar 底部留白 */
  embedded?: boolean;
}> = observer(
  ({
    spaceId,
    spaceProductId,
    productDisplayName,
    spaceDisplayName,
    embedded = false,
  }) => {
    const navigate = useNavigate();
    const model = useMemo(() => new CompanionRoomModel(), []);
    const shellRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    const safeTopPad = useSafeAreaTopPad(headerRef, {
      pauseWhile: () => document.documentElement.classList.contains("lf-h5-cs-keyboard-open"),
    });
    const scrollRef = useRef<HTMLDivElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const stickBottomRef = useRef(true);
    const {
      onInputFocus: openKeyboardLayout,
      onInputBlur,
      onComposePointerDown,
      closeKeyboardLayout,
    } = useCsKeyboardLayout(shellRef);

    const scrollChatToBottom = useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    }, []);

    const [draft, setDraft] = useState("");
    const [composeReply, setComposeReply] = useState<ComposeReply | null>(null);
    const [composeImage, setComposeImage] = useState<ComposeImageDraft | null>(null);
    const [uploadingImg, setUploadingImg] = useState(false);
    const [pdfPreview, setPdfPreview] = useState<{ url: string; title: string } | null>(null);
    const [threadDetail, setThreadDetail] = useState<MyMessageThreadRow | null>(null);
    /** 点击消息后才展示「回复 / 引用」 */
    const [actionMessageId, setActionMessageId] = useState<string | null>(null);
    const [stickerPanelOpen, setStickerPanelOpen] = useState(false);
    const [stickerGroups, setStickerGroups] = useState<ChatStickerGroupBundle[]>([]);
    const [activeStickerGroupId, setActiveStickerGroupId] = useState("");
    const [stickersLoading, setStickersLoading] = useState(false);
    const stickersLoadedRef = useRef(false);
    const [mentionProducts, setMentionProducts] = useState<ChatMentionProduct[]>([]);

    useEffect(() => {
      const sid = spaceId.trim();
      if (!sid) return;
      let cancelled = false;
      void SpaceApi.getStartupConfig(sid).then((list) => {
        if (cancelled) return;
        setMentionProducts(toChatMentionProductsFromStartup(list));
      });
      return () => {
        cancelled = true;
      };
    }, [spaceId]);

    const handleMentionProductClick = useCallback(
      (product: ChatMentionProduct) => {
        const pid = product.spaceProductId.trim();
        const sid = spaceId.trim();
        if (!pid || !sid) return;
        const catalogHit = mentionProducts.find((p) => p.spaceProductId.trim() === pid);
        const name =
          catalogHit?.displayName?.trim() || product.displayName?.trim() || "该产品";
        const kind = catalogHit?.productKind ?? product.productKind;
        const isProductBundle = kind?.trim().toLowerCase() === "product_bundle";
        const targetPath =
          isProductBundle
            ? `/space/${sid}/subscribe/${encodeURIComponent(pid)}`
            : `/space/${sid}/${encodeURIComponent(pid)}`;
        // 已在该群聊/产品页：跳同地址无任何变化，需明确提示
        if (!isProductBundle && pid === spaceProductId.trim()) {
          antdMessage.info(`当前已在「${name}」`);
          return;
        }
        if (!isProductBundle && !isH5MobileProductSupported(kind)) {
          showDesktopOnlyTipModal();
          return;
        }
        setActionMessageId(null);
        showChatMentionNavigateModal({
          productName: name,
          onConfirm: () => {
            h5NavigateTo(navigate, targetPath);
          },
        });
      },
      [navigate, spaceId, spaceProductId, mentionProducts]
    );

    /** 滚动区捕获阶段委托：不依赖内层 button 冒泡（touch-action:pan-y 场景更稳） */
    useEffect(() => {
      if (model.sidePanel) return;
      const root = scrollRef.current;
      if (!root) return;
      const onCaptureClick = (e: MouseEvent) => {
        if (!handleChatMentionTargetClick(e.target, handleMentionProductClick)) return;
        e.preventDefault();
        e.stopPropagation();
      };
      root.addEventListener("click", onCaptureClick, true);
      return () => root.removeEventListener("click", onCaptureClick, true);
    }, [handleMentionProductClick, model.sidePanel]);

    const mentionMsgProps = {
      mentionProducts,
      onMentionProductClick: handleMentionProductClick,
    };

    /** 键盘弹起后壳层高度会延后变化，补几次滚底保证贴底 */
    const onInputFocus = useCallback(() => {
      stickBottomRef.current = true;
      setStickerPanelOpen(false);
      openKeyboardLayout();
      scrollChatToBottom();
      requestAnimationFrame(scrollChatToBottom);
      window.setTimeout(scrollChatToBottom, 50);
      window.setTimeout(scrollChatToBottom, 120);
      window.setTimeout(scrollChatToBottom, 240);
      window.setTimeout(scrollChatToBottom, 400);
    }, [openKeyboardLayout, scrollChatToBottom]);

    useEffect(() => {
      if (!globalModel.isLoggedIn || !spaceId.trim() || !spaceProductId.trim()) return;
      void model.init(spaceId.trim(), globalModel.currentUser?.id ?? "", spaceProductId.trim());
      return () => {
        model.dispose();
      };
    }, [spaceId, spaceProductId, model, globalModel.isLoggedIn, globalModel.currentUser?.id]);

    const latestServerMsgId = useMemo(
      () => companionLatestServerMsgId(model.chatMessages),
      [model.chatMessages]
    );

    /**
     * 停留在聊天室时：新消息仍会给参与者 unread++，需持续 markRead。
     * 仅进房时清一次不够；离开前再刷一次（不传游标，按房间最新消息清零）。
     */
    useEffect(() => {
      if (!globalModel.isLoggedIn) return;
      const sid = spaceId.trim();
      const pid = spaceProductId.trim();
      if (!sid || !pid) return;
      const key = `group:${pid}`;
      const timer = window.setTimeout(() => {
        void SpaceMessageCenterApi.markRead(sid, key, latestServerMsgId ?? null);
      }, 300);
      return () => window.clearTimeout(timer);
    }, [spaceId, spaceProductId, latestServerMsgId, globalModel.isLoggedIn]);

    useEffect(() => {
      const sid = spaceId.trim();
      const pid = spaceProductId.trim();
      return () => {
        if (!sid || !pid) return;
        // 离开房间：按房间 lastMessageId 清未读，避免本地列表略滞后漏清
        void SpaceMessageCenterApi.markRead(sid, `group:${pid}`);
      };
    }, [spaceId, spaceProductId]);

    useEffect(() => {
      setActionMessageId(null);
    }, [model.sidePanel]);

    const messageById = useMemo(() => {
      const map = new Map<string, CompanionMessageRow>();
      for (const row of model.chatMessages) {
        map.set(row.id, row);
      }
      return map;
    }, [model.chatMessages]);

    const resolveReplyTargetName = useCallback(
      (target: CompanionMessageRow | null | undefined, selfName: string): string => {
        if (!target) return "用户";
        return messageSenderLabel(target, selfName, globalModel.currentUser?.id);
      },
      []
    );

    const chatLen = model.chatMessages.length;
    useEffect(() => {
      if (!stickBottomRef.current) return;
      scrollChatToBottom();
    }, [chatLen, model.sidePanel, scrollChatToBottom]);

    const onChatScroll = (): void => {
      const el = scrollRef.current;
      if (!el) return;
      stickBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      if (el.scrollTop <= 24 && model.hasOlderChat && !model.loadingOlderChat) {
        const prevHeight = el.scrollHeight;
        void model.loadOlderChat().then(() => {
          requestAnimationFrame(() => {
            const next = scrollRef.current;
            if (next) next.scrollTop = next.scrollHeight - prevHeight;
          });
        });
      }
    };

    const sendComposer = async (): Promise<void> => {
      if (!model.chatOpen) {
        antdMessage.warning("当前不可发言");
        return;
      }
      const afterSendScroll = (): void => {
        stickBottomRef.current = true;
        scrollChatToBottom();
        requestAnimationFrame(scrollChatToBottom);
        window.setTimeout(scrollChatToBottom, 50);
        window.setTimeout(scrollChatToBottom, 120);
      };
      if (composeImage) {
        await model.sendImage({
          fileUrl: composeImage.fileUrl,
          fileName: composeImage.fileName,
          replyToMessageId: composeReply?.messageId,
        });
        setComposeImage(null);
        setComposeReply(null);
        afterSendScroll();
        return;
      }
      const text = draft.trim();
      if (!text) {
        antdMessage.warning("请输入内容");
        return;
      }
      await model.sendText({
        content: text,
        replyToMessageId: composeReply?.messageId,
      });
      setDraft("");
      setComposeReply(null);
      afterSendScroll();
    };

    const uploadImageFile = async (f: File): Promise<void> => {
      if (
        model.maxImagePerDay != null &&
        model.maxImagePerDay > 0 &&
        (model.remainImagePerDay ?? 0) <= 0
      ) {
        antdMessage.warning("今日发送图片次数已达上限");
        return;
      }
      if (composeImage) {
        antdMessage.warning("已选择图片，请先移除后再添加");
        return;
      }
      setUploadingImg(true);
      try {
        // 与客服通道一致：先压成 JPEG，避免微信/iOS 相册 HEIC、超大原图直传 OSS 出现 Load failed
        const uploadFile = await prepareCompanionImageUploadFile(f);
        const url = await uploadAppFileToOss(uploadFile, "COMPANION_CHAT_IMAGE");
        setComposeImage({
          fileUrl: url,
          fileName: f.name.trim() || "图片",
        });
      } catch (e) {
        antdMessage.error(companionUploadErrorMessage(e));
      } finally {
        setUploadingImg(false);
      }
    };

    const loadStickers = useCallback(async (): Promise<void> => {
      if (stickersLoadedRef.current) return;
      setStickersLoading(true);
      try {
        const res = await ChatStickerApi.listActiveCatalog();
        const groups = (Array.isArray(res.groups) ? res.groups : []).map((g) => ({
          ...g,
          stickers: (g.stickers ?? []).filter((r) => r.imageUrl?.trim()),
        }));
        setStickerGroups(groups);
        setActiveStickerGroupId((prev) => {
          if (prev && groups.some((g) => g.id === prev)) return prev;
          return groups[0]?.id ?? "";
        });
        stickersLoadedRef.current = true;
      } catch (e) {
        antdMessage.error(e instanceof Error ? e.message : "表情加载失败");
      } finally {
        setStickersLoading(false);
      }
    }, []);

    const toggleStickerPanel = useCallback(() => {
      setStickerPanelOpen((open) => {
        const next = !open;
        if (next) {
          closeKeyboardLayout();
          void loadStickers();
        }
        return next;
      });
    }, [closeKeyboardLayout, loadStickers]);

    const sendSticker = useCallback(
      async (sticker: ChatStickerItem): Promise<void> => {
        if (!model.chatOpen || model.sending) return;
        const raw = sticker.imageUrl?.trim();
        if (!raw) return;
        const url = stripOssImageProcess(raw);
        try {
          await model.sendSticker({
            fileUrl: url,
            fileName: sticker.name?.trim() || "表情",
            replyToMessageId: composeReply?.messageId,
          });
          setComposeReply(null);
          setStickerPanelOpen(false);
          stickBottomRef.current = true;
          requestAnimationFrame(scrollChatToBottom);
          window.setTimeout(scrollChatToBottom, 50);
        } catch (e) {
          antdMessage.error(e instanceof Error ? e.message : "发送失败");
        }
      },
      [composeReply?.messageId, model, scrollChatToBottom]
    );

    const startReply = useCallback(
      (m: CompanionMessageRow): void => {
        if (Boolean(m.deleted) || Boolean(m.shielded)) return;
        if (Boolean(m.fromUser) && companionIsSelfModerationRejected(m)) return;
        if (model.hasUndeliveredOutboundRetry(m.id)) return;
        const selfName = globalModel.currentUser?.name?.trim() || "我";
        setComposeReply({
          messageId: m.id,
          senderLabel: messageSenderLabel(m, selfName, globalModel.currentUser?.id),
          quotedFull: pinPreviewLine(m),
        });
        setActionMessageId(null);
      },
      [model]
    );

    const userName = globalModel.currentUser?.name ?? "我";
    const userAvatar = resolveAppMediaUrl(globalModel.currentUser?.avatarUrl);
    const title =
      productDisplayName?.trim() ||
      model.roomTitle?.trim() ||
      spaceDisplayName?.trim() ||
      "群聊";
    const latestPinned = model.pinnedList[0];
    const canSend = model.chatOpen && !model.sending && (Boolean(composeImage) || Boolean(draft.trim()));
    const imagePickDisabled =
      !model.chatOpen ||
      model.sending ||
      Boolean(composeImage) ||
      uploadingImg ||
      (model.maxImagePerDay != null &&
        model.maxImagePerDay > 0 &&
        (model.remainImagePerDay ?? 0) <= 0);

    const quotaParts: string[] = [];
    if (model.maxPerDay != null && model.maxPerDay > 0) {
      quotaParts.push(`今日还剩${model.remainPerDay ?? 0}次发言`);
    }
    if (model.maxImagePerDay != null && model.maxImagePerDay > 0) {
      quotaParts.push(`图片还剩${model.remainImagePerDay ?? 0}次`);
    }
    const quotaText = quotaParts.join(" · ");
    const showTipBar = !stickerPanelOpen && (Boolean(composeReply) || Boolean(quotaText));
    const stickerBtnDisabled = !model.chatOpen || model.sending || uploadingImg;
    const activeStickerGroup =
      stickerGroups.find((g) => g.id === activeStickerGroupId) ?? stickerGroups[0] ?? null;
    const panelStickers = activeStickerGroup?.stickers ?? [];

    const tipBarStyle: CSSProperties = composeReply
      ? {
          height: COMPANION_REPLY_TIP_BAR.height,
          minHeight: COMPANION_REPLY_TIP_BAR.height,
          fontSize: COMPANION_REPLY_TIP_BAR.fontSize,
          color: COMPANION_REPLY_TIP_BAR.color,
        }
      : {
          height: COMPANION_QUOTA_BAR.height,
          minHeight: COMPANION_QUOTA_BAR.height,
          fontSize: COMPANION_QUOTA_BAR.fontSize,
          color: COMPANION_QUOTA_BAR.color,
        };

    const canReplyOrQuote = (m: CompanionMessageRow): boolean => {
      if (!model.chatOpen) return false;
      if (Boolean(m.deleted) || Boolean(m.shielded)) return false;
      if (Boolean(m.fromUser) && companionIsSelfModerationRejected(m)) return false;
      if (model.hasUndeliveredOutboundRetry(m.id)) return false;
      return true;
    };

    const pageStyle: CSSProperties | undefined =
      !embedded && safeTopPad > 0
        ? ({ "--space-cs-header-safe-top-pad": `${safeTopPad}px` } as CSSProperties)
        : undefined;

    const renderChatMessage = (m: CompanionMessageRow, idx: number): JSX.Element => {
      const prev = idx > 0 ? model.chatMessages[idx - 1] : undefined;
      const selfUserId = globalModel.currentUser?.id ?? "";
      const isSelf = companionMessageIsMine(m, selfUserId);
      const isStaff = !Boolean(m.fromUser);
      const isAssistantSystem = companionIsAssistantSystemMessage(m);
      const peerName = m.senderName?.trim() || (isStaff ? "老师" : "用户");
      const roleLabel = isStaff && !isAssistantSystem ? companionStaffRoleLabel(m.senderSpaceRole) : "";
      const displayName = roleLabel ? `${peerName}（${roleLabel}）` : peerName;
      const isSpaceTeacher =
        isStaff && !isAssistantSystem && (m.senderSpaceRole ?? "").trim().toLowerCase() === "teacher";
      const showFail = isSelf && model.hasUndeliveredOutboundRetry(m.id);
      const showReject = isSelf && companionIsSelfModerationRejected(m);
      const canAct = canReplyOrQuote(m);
      const showAction = canAct && actionMessageId === m.id;
      const replyToId = m.replyToMessageId?.trim() || "";
      const replyTo = replyToId ? messageById.get(replyToId) ?? null : null;
      const replyToName = replyToId
        ? resolveReplyTargetName(replyTo, userName)
        : undefined;

      return (
        <div key={m.id}>
          {shouldShowTimeDivider(prev, m) ? (
            <div className="space-cs-time">{getReadableTime(m.sentAt ?? "")}</div>
          ) : null}
          <div
            className={`space-cs-msg-row${isSelf ? " space-cs-msg-row--self" : ""}`}
            onClick={(e) => {
              if (handleChatMentionTargetClick(e.target, handleMentionProductClick)) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              if (isChatMentionClickTarget(e.target)) return;
              e.stopPropagation();
              if (!canAct) return;
              setActionMessageId((prevId) => (prevId === m.id ? null : m.id));
            }}
          >
            <EasyAvatar
              shape="square"
              squareRadius={4}
              size={36}
              url={
                isAssistantSystem
                  ? CS_ASSISTANT_AVATAR_URL
                  : isSelf
                    ? resolveAppMediaUrl(m.senderAvatarUrl) || userAvatar || undefined
                    : resolveAppMediaUrl(m.senderAvatarUrl) || undefined
              }
              word={avatarDisplayWord(isSelf ? userName : peerName)}
              alt=""
            />
            <div className="space-cs-msg-col">
              {!isSelf ? (
                <div
                  className={`space-cs-msg-name${
                    isSpaceTeacher ? " space-cs-msg-name--teacher" : ""
                  }`}
                >
                  <MemberNickname nickname={displayName} badges={m.senderMemberBadges} />
                </div>
              ) : null}
              <div
                className={`space-cs-msg-body-line${isSelf ? " space-cs-msg-body-line--self" : ""}`}
              >
                {isSelf && showAction ? (
                  <button
                    type="button"
                    className="h5-crm-msg-action h5-crm-msg-action--quote"
                    style={{
                      color: COMPANION_REPLY_ACTION.color,
                      fontSize: COMPANION_REPLY_ACTION.fontSize,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      startReply(m);
                    }}
                  >
                    引用
                  </button>
                ) : null}
                {showFail || showReject ? (
                  <button
                    type="button"
                    className="space-cs-outbound-fail-hit"
                    aria-label={showFail ? "发送失败" : "审核未通过"}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (showFail) {
                        void model.retryOutboundMessage(m.id);
                      } else {
                        antdMessage.info("该消息未通过审核，当前仅您自己可见。");
                      }
                    }}
                  >
                    <ExclamationCircleFilled className="space-cs-outbound-fail-icon" />
                  </button>
                ) : null}
                <div
                  className={`space-cs-msg-bubble${isSelf ? " space-cs-msg-bubble--self" : ""}${
                    m.messageType === "IMAGE" || m.messageType === "STICKER"
                      ? " space-cs-msg-bubble--image"
                      : ""
                  }`}
                >
                  <CompanionRoomMessageContent
                    m={m}
                    onOpenPdf={(url, name) => setPdfPreview({ url, title: name })}
                    {...mentionMsgProps}
                  />
                </div>
                {!isSelf && showAction ? (
                  <button
                    type="button"
                    className="h5-crm-msg-action h5-crm-msg-action--reply"
                    style={{
                      color: COMPANION_REPLY_ACTION.color,
                      fontSize: COMPANION_REPLY_ACTION.fontSize,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      startReply(m);
                    }}
                  >
                    回复
                  </button>
                ) : null}
              </div>
              {replyToId ? (
                <CompanionReplyQuoteBlock
                  name={replyToName?.trim() || "用户"}
                  preview={replyTo ? quotePreviewText(replyTo) : "引用消息"}
                  alignSelf={isSelf}
                />
              ) : null}
            </div>
          </div>
        </div>
      );
    };

    if (!globalModel.isLoggedIn) {
      return (
        <div className="space-product-page">
          <div className="h5-crm-empty">请先登录后查看群聊。</div>
        </div>
      );
    }

    if (model.loading && !model.roomEnabled && !model.loadError) {
      return (
        <div className="space-product-page">
          <div className="h5-crm-empty">
            <Spin />
          </div>
        </div>
      );
    }

    if (model.loadError) {
      return (
        <div className="space-product-page">
          <div className="h5-crm-empty">加载失败，请稍后重试。</div>
        </div>
      );
    }

    if (!model.roomEnabled) {
      return (
        <div className="space-product-page">
          <div className="h5-crm-empty">当前空间未开通群聊陪伴。</div>
        </div>
      );
    }

    if (!model.chatRoomId) {
      return (
        <div className="space-product-page">
          <div className="h5-crm-empty">管理员暂未创建文字直播房间，请稍后再试。</div>
        </div>
      );
    }

    return (
      <div
        className={`space-cs-page h5-crm-page${embedded ? " h5-crm-page--embedded" : ""}`}
        style={pageStyle}
      >
        <div ref={shellRef} className="space-cs-page-shell">
          <header ref={headerRef} className="h5-crm-tabs">
            <button
              type="button"
              className={`h5-crm-tab${!model.sidePanel ? " is-active" : ""}`}
              onClick={() => model.selectSidePanel(null)}
            >
              <span className="h5-crm-tab-label">{title}</span>
            </button>
            <button
              type="button"
              className={`h5-crm-tab${model.sidePanel === "mine" ? " is-active" : ""}`}
              onClick={() => model.selectSidePanel("mine")}
            >
              我的发言
            </button>
            <button
              type="button"
              className={`h5-crm-tab${model.sidePanel === "files" ? " is-active" : ""}`}
              onClick={() => model.selectSidePanel("files")}
            >
              学习资料
            </button>
            <button
              type="button"
              className={`h5-crm-tab${model.sidePanel === "pinned" ? " is-active" : ""}`}
              onClick={() => model.selectSidePanel("pinned")}
            >
              置顶消息
            </button>
          </header>

          {latestPinned && model.sidePanel !== "pinned" ? (
            <button
              type="button"
              className="h5-crm-pin-strip"
              onClick={() => model.selectSidePanel("pinned")}
            >
              <PushpinOutlined className="h5-crm-pin-strip-ic" />
              <span className="h5-crm-pin-strip-marquee">
                <span className="h5-crm-pin-strip-marquee-inner">
                  {pinPreviewLine(latestPinned)}
                </span>
              </span>
            </button>
          ) : null}

          {model.sidePanel ? (
            <div className="h5-crm-side">
              <div className="h5-crm-side-body">
                {model.sidePanel === "mine" ? (
                  <div
                    className="h5-crm-side-scroll"
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      if (
                        el.scrollHeight - el.scrollTop - el.clientHeight < 48 &&
                        model.mineHasMore &&
                        !model.mineLoadingMore
                      ) {
                        void model.loadMoreMine();
                      }
                    }}
                  >
                    {model.mineLoading && model.mineThreads.length === 0 ? (
                      <div className="space-cs-loading">
                        <Spin />
                      </div>
                    ) : null}
                    {model.mineThreads.map((t) => (
                      <button
                        key={t.myMessage.id}
                        type="button"
                        className="h5-crm-mine-card"
                        onClick={() => setThreadDetail(t)}
                      >
                        <div className="h5-crm-mine-content">
                          {pinPreviewLine(t.myMessage)}
                        </div>
                        <div className="h5-crm-mine-meta">
                          {getReadableTime(t.myMessage.sentAt ?? "")}
                          {t.replyCount ? ` · ${t.replyCount} 条回复` : ""}
                        </div>
                      </button>
                    ))}
                    {model.mineLoadingMore ? (
                      <div className="space-cs-loading-top">加载更多…</div>
                    ) : null}
                    {!model.mineLoading && model.mineThreads.length === 0 ? (
                      <div className="h5-crm-empty-inline">暂无发言</div>
                    ) : null}
                  </div>
                ) : null}

                {model.sidePanel === "files" ? (
                  <div
                    className="h5-crm-side-scroll"
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      if (
                        el.scrollHeight - el.scrollTop - el.clientHeight < 48 &&
                        model.filesHasMore &&
                        !model.filesLoadingMore
                      ) {
                        void model.loadMoreFiles();
                      }
                    }}
                  >
                    {model.filesLoading && model.pdfFiles.length === 0 ? (
                      <div className="space-cs-loading">
                        <Spin />
                      </div>
                    ) : null}
                    {model.pdfFiles.map((f) => (
                      <button
                        key={f.messageId}
                        type="button"
                        className="h5-crm-file-card"
                        onClick={() =>
                          setPdfPreview({
                            url: resolveAppMediaUrl(f.fileUrl) ?? f.fileUrl,
                            title: f.fileName || "PDF",
                          })
                        }
                      >
                        <FilePdfOutlined className="h5-crm-pdf-ic" />
                        <div className="h5-crm-file-main">
                          <div className="h5-crm-file-name">{f.fileName || "PDF"}</div>
                          <div className="h5-crm-file-meta">
                            {f.senderName} · {getReadableTime(f.sentAt)}
                          </div>
                        </div>
                      </button>
                    ))}
                    {model.filesLoadingMore ? (
                      <div className="space-cs-loading-top">加载更多…</div>
                    ) : null}
                    {!model.filesLoading && model.pdfFiles.length === 0 ? (
                      <div className="h5-crm-empty-inline">暂无学习资料</div>
                    ) : null}
                  </div>
                ) : null}

                {model.sidePanel === "pinned" ? (
                  <div className="h5-crm-side-scroll">
                    {model.pinnedLoading && model.pinnedList.length === 0 ? (
                      <div className="space-cs-loading">
                        <Spin />
                      </div>
                    ) : null}
                    {model.pinnedList.map((m) => (
                      <div key={m.id} className="h5-crm-pin-card">
                        <div className="h5-crm-pin-card-name">
                          {messageSenderLabel(m, userName, globalModel.currentUser?.id)}
                        </div>
                        <CompanionRoomMessageContent
                          m={m}
                          onOpenPdf={(url, name) => setPdfPreview({ url, title: name })}
                          {...mentionMsgProps}
                        />
                        <div className="h5-crm-mine-meta">{getReadableTime(m.sentAt ?? "")}</div>
                      </div>
                    ))}
                    {!model.pinnedLoading && model.pinnedList.length === 0 ? (
                      <div className="h5-crm-empty-inline">暂无置顶</div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="space-cs-chat">
              <div
                ref={scrollRef}
                className="space-cs-scroll"
                onScroll={onChatScroll}
                onClick={(e) => {
                  if (handleChatMentionTargetClick(e.target, handleMentionProductClick)) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  if (isChatMentionClickTarget(e.target)) return;
                  setActionMessageId(null);
                }}
              >
                <div className="space-cs-scroll-inner">
                  {model.loadingOlderChat ? (
                    <div className="space-cs-loading-top">加载更早消息…</div>
                  ) : null}
                  {!model.hasOlderChat && model.chatMessages.length >= model.maxDisplayCount ? (
                    <div className="space-cs-history-tip">已达最多显示条数上限</div>
                  ) : null}
                  {model.loading && model.chatMessages.length === 0 ? (
                    <div className="space-cs-loading">
                      <Spin />
                    </div>
                  ) : null}
                  {model.chatMessages.map((m, idx) => renderChatMessage(m, idx))}
                </div>
              </div>

              {showTipBar ? (
                <div
                  className={`h5-crm-quota${composeReply ? " h5-crm-quota--reply" : ""}`}
                  style={tipBarStyle}
                >
                  {composeReply ? (
                    <span className="h5-crm-quota-text">
                      <span className="h5-crm-quota-prefix">回复 </span>
                      <span
                        className="h5-crm-quota-name"
                        style={{ color: COMPANION_REPLY_TIP_BAR.nameColor }}
                      >
                        {composeReply.senderLabel}
                      </span>
                      <span className="h5-crm-quota-body">
                        {" "}：{composeReply.quotedFull}
                      </span>
                    </span>
                  ) : (
                    <span className="h5-crm-quota-text">{quotaText}</span>
                  )}
                  {composeReply ? (
                    <button
                      type="button"
                      className="h5-crm-quota-cancel"
                      style={{ color: COMPANION_REPLY_TIP_BAR.cancelColor }}
                      onClick={() => setComposeReply(null)}
                    >
                      取消
                    </button>
                  ) : null}
                </div>
              ) : null}

              {stickerPanelOpen ? (
                <div className="h5-crm-sticker-panel" aria-label="表情面板">
                  {stickersLoading ? (
                    <div className="h5-crm-sticker-panel-empty">
                      <Spin size="small" />
                    </div>
                  ) : stickerGroups.length === 0 ? (
                    <div className="h5-crm-sticker-panel-empty">暂无表情</div>
                  ) : (
                    <>
                      <div className="h5-crm-sticker-tabs" role="tablist">
                        {stickerGroups.map((g) => (
                          <button
                            key={g.id}
                            type="button"
                            role="tab"
                            aria-selected={g.id === (activeStickerGroup?.id ?? "")}
                            className={`h5-crm-sticker-tab${
                              g.id === (activeStickerGroup?.id ?? "")
                                ? " h5-crm-sticker-tab--active"
                                : ""
                            }`}
                            onClick={() => setActiveStickerGroupId(g.id)}
                          >
                            {g.name}
                          </button>
                        ))}
                      </div>
                      {panelStickers.length === 0 ? (
                        <div className="h5-crm-sticker-panel-empty h5-crm-sticker-panel-empty--body">
                          该分组暂无表情
                        </div>
                      ) : (
                        <div
                          className="h5-crm-sticker-grid"
                          style={
                            {
                              ["--crm-sticker-size" as string]: `${COMPANION_STICKER_PANEL.stickerSize}px`,
                              ["--crm-sticker-gap" as string]: `${COMPANION_STICKER_PANEL.gap}px`,
                              ["--crm-sticker-columns" as string]: String(
                                COMPANION_STICKER_PANEL.columns
                              ),
                            } as CSSProperties
                          }
                        >
                          {panelStickers.map((s) => (
                            <button
                              key={s.id}
                              type="button"
                              className="h5-crm-sticker-item"
                              disabled={!model.chatOpen || model.sending}
                              onClick={() => void sendSticker(s)}
                            >
                              <img
                                src={
                                  stripOssImageProcess(
                                    resolveAppMediaUrl(s.imageUrl) ?? s.imageUrl
                                  )
                                }
                                alt=""
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : null}

              <div className="space-cs-compose h5-crm-compose">
                {composeImage ? (
                  <div className="h5-crm-compose-img-preview">
                    <img
                      src={resolveAppMediaUrl(composeImage.fileUrl) ?? composeImage.fileUrl}
                      alt=""
                    />
                    <button type="button" onClick={() => setComposeImage(null)}>
                      移除
                    </button>
                  </div>
                ) : null}
                <div className="h5-crm-compose-row">
                  <div className="space-cs-compose-input-wrap">
                    <Input.TextArea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onFocus={onInputFocus}
                      onBlur={onInputBlur}
                      placeholder={model.chatOpen ? "输入消息" : "当前不可发言"}
                      disabled={!model.chatOpen || model.sending || Boolean(composeImage)}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      className="space-cs-compose-input"
                    />
                  </div>
                  <div className="h5-crm-compose-tools">
                    <button
                      type="button"
                      className={`h5-crm-compose-sticker-btn${
                        stickerPanelOpen ? " h5-crm-compose-sticker-btn--active" : ""
                      }`}
                      disabled={stickerBtnDisabled}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        onComposePointerDown();
                      }}
                      onClick={() => toggleStickerPanel()}
                      aria-label="表情"
                      aria-pressed={stickerPanelOpen}
                    >
                      <span className="h5-crm-compose-sticker-icon" aria-hidden>
                        <span className="h5-crm-compose-sticker-mouth" />
                      </span>
                    </button>
                    <button
                      type="button"
                      className="space-cs-compose-img-btn"
                      disabled={imagePickDisabled}
                      onClick={() => {
                        setStickerPanelOpen(false);
                        imageInputRef.current?.click();
                      }}
                      aria-label="发送图片"
                    >
                      <span className="space-cs-compose-plus-icon" aria-hidden />
                    </button>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
                      className="space-cs-compose-file-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (!file) return;
                        if (!file.type.startsWith("image/")) {
                          antdMessage.warning("请选择图片文件");
                          return;
                        }
                        void uploadImageFile(file);
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className={`space-cs-send-btn${canSend ? " space-cs-send-btn--active" : ""}`}
                    disabled={!canSend}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      onComposePointerDown();
                    }}
                    onClick={() => {
                      void sendComposer().then(() => {
                        closeKeyboardLayout();
                      });
                    }}
                  >
                    发送
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <Modal
          open={Boolean(pdfPreview)}
          title={pdfPreview?.title || "PDF"}
          footer={null}
          onCancel={() => setPdfPreview(null)}
          width="100%"
          style={{ top: 24, maxWidth: 720, margin: "0 auto" }}
          styles={{ body: { height: "70vh", padding: 0 } }}
          destroyOnHidden
        >
          {pdfPreview ? (
            <iframe
              title={pdfPreview.title}
              src={pdfPreview.url}
              style={{ width: "100%", height: "70vh", border: 0 }}
            />
          ) : null}
        </Modal>

        <Modal
          open={Boolean(threadDetail)}
          title="发言详情"
          footer={null}
          onCancel={() => setThreadDetail(null)}
          destroyOnHidden
        >
          {threadDetail ? (
            <div className="h5-crm-thread">
              <CompanionRoomMessageContent
                m={threadDetail.myMessage}
                onOpenPdf={(url, name) => setPdfPreview({ url, title: name })}
                {...mentionMsgProps}
              />
              {threadDetail.myMessage.replyToMessageId ? (
                <CompanionReplyQuoteBlock
                  name={resolveReplyTargetName(
                    messageById.get(threadDetail.myMessage.replyToMessageId.trim()) ?? null,
                    userName
                  )}
                  preview={(() => {
                    const t = messageById.get(threadDetail.myMessage.replyToMessageId!.trim());
                    return t ? quotePreviewText(t) : "引用消息";
                  })()}
                  alignSelf
                />
              ) : null}
              <div className="h5-crm-mine-meta">
                {getReadableTime(threadDetail.myMessage.sentAt ?? "")}
              </div>
              {(threadDetail.replies ?? []).map((r) => {
                const replyParent =
                  r.replyToMessageId?.trim() === threadDetail.myMessage.id
                    ? threadDetail.myMessage
                    : r.replyToMessageId
                      ? messageById.get(r.replyToMessageId.trim()) ?? null
                      : null;
                return (
                  <div key={r.id} className="h5-crm-thread-reply">
                    <div className="h5-crm-pin-card-name">
                      {messageSenderLabel(r, userName, globalModel.currentUser?.id)}
                    </div>
                    <CompanionRoomMessageContent
                      m={r}
                      onOpenPdf={(url, name) => setPdfPreview({ url, title: name })}
                      {...mentionMsgProps}
                    />
                    {r.replyToMessageId ? (
                      <CompanionReplyQuoteBlock
                        name={resolveReplyTargetName(replyParent, userName)}
                        preview={replyParent ? quotePreviewText(replyParent) : "引用消息"}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
        </Modal>
      </div>
    );
  }
);

export default CompanionRoomPage;
